/**
 * Colors Module - Ported from verachem3/graphics/molecularGraphics.py
 *
 * Provides color schemes for molecular visualization:
 * - Element-based coloring (Jmol/CPK style)
 * - Residue type coloring (hydrophobic vs polar)
 * - Residue charge coloring (positive/negative/neutral)
 * - Partial charge gradient coloring
 */

// Type for RGB color tuple (0-1 range)
export type RGBColor = [number, number, number];

// Type for hex color string
export type HexColor = string;

// ============================================================================
// Element Colors (from molecularGraphics.py:45-119)
// ============================================================================

/**
 * Element colors matching desktop PF MolView
 * RGB values in 0-1 range, ported from molecularGraphics.py tempDict
 */
export const ELEMENT_COLORS: Record<string, RGBColor> = {
  H: [0.6, 0.6, 0.6],
  He: [1.0, 0.784314, 0.784314],
  Li: [0.647059, 0.164706, 0.164706],
  Be: [1.0, 0.078431, 0.576471],
  B: [0.0, 1.0, 0.0],
  C: [0.0, 0.8, 0.0],
  N: [0.2, 0.2, 0.9],
  O: [0.941176, 0.0, 0.0],
  F: [0.784314, 0.647059, 0.094118],
  Ne: [1.0, 0.078431, 0.576471],
  Na: [0.0, 0.0, 1.0],
  Mg: [0.164706, 0.501961, 0.164706],
  Al: [0.501961, 0.501961, 0.564706],
  Si: [0.784314, 0.647059, 0.094118],
  P: [1.0, 0.647059, 0.0],
  S: [1.0, 0.784314, 0.196078],
  Cl: [0.0, 1.0, 0.0],
  Ar: [1.0, 0.078431, 0.576471],
  K: [1.0, 0.078431, 0.576471],
  Ca: [0.501961, 0.501961, 0.564706],
  Fe: [1.0, 0.647059, 0.0],
  Ni: [0.647059, 0.164706, 0.164706],
  Cu: [0.647059, 0.164706, 0.164706],
  Zn: [0.647059, 0.164706, 0.164706],
  Br: [0.647059, 0.164706, 0.164706],
  I: [0.560784, 0.560784, 1.0],
  Au: [0.784314, 0.647059, 0.094118],
  Ag: [0.501961, 0.501961, 0.564706],
  // Default for unknown elements
  default: [0.75, 0.75, 0.75],
};

// ============================================================================
// Residue Type Colors (from molecularGraphics.py:123-145)
// Hydrophobic vs Polar distinction
// ============================================================================

/**
 * Residue type colors - distinguishes hydrophobic from polar residues
 * Yellow = hydrophobic, Blue-ish = polar/charged
 */
export const RESIDUE_TYPE_COLORS: Record<string, RGBColor> = {
  // Hydrophobic (yellow)
  ALA: [0.81, 0.81, 0.08],
  VAL: [0.81, 0.81, 0.08],
  PHE: [0.81, 0.81, 0.08],
  PRO: [0.81, 0.81, 0.08],
  MET: [0.81, 0.81, 0.08],
  ILE: [0.81, 0.81, 0.08],
  LEU: [0.81, 0.81, 0.08],
  GLY: [0.81, 0.81, 0.08],
  TRP: [0.81, 0.81, 0.08],
  // Polar/Charged (blue-ish)
  ASP: [0.59, 0.59, 0.99],
  GLU: [0.59, 0.59, 0.99],
  LYS: [0.59, 0.59, 0.99],
  ARG: [0.59, 0.59, 0.99],
  SER: [0.59, 0.59, 0.99],
  THR: [0.59, 0.59, 0.99],
  TYR: [0.59, 0.59, 0.99],
  HIS: [0.59, 0.59, 0.99],
  CYS: [0.59, 0.59, 0.99],
  ASN: [0.59, 0.59, 0.99],
  GLN: [0.59, 0.59, 0.99],
  // Default
  default: [0.4, 0.4, 0.4],
};

// ============================================================================
// Residue Charge Colors (from molecularGraphics.py:147-169)
// Based on amino acid formal charge at physiological pH
// ============================================================================

/**
 * Residue charge colors - based on formal charge at pH 7
 * Red = negative (ASP, GLU)
 * Blue = positive (LYS, ARG)
 * Yellow = neutral
 */
export const RESIDUE_CHARGE_COLORS: Record<string, RGBColor> = {
  // Neutral (yellow)
  ALA: [0.81, 0.81, 0.08],
  VAL: [0.81, 0.81, 0.08],
  PHE: [0.81, 0.81, 0.08],
  PRO: [0.81, 0.81, 0.08],
  MET: [0.81, 0.81, 0.08],
  ILE: [0.81, 0.81, 0.08],
  LEU: [0.81, 0.81, 0.08],
  GLY: [0.81, 0.81, 0.08],
  SER: [0.81, 0.81, 0.08],
  THR: [0.81, 0.81, 0.08],
  TYR: [0.81, 0.81, 0.08],
  HIS: [0.81, 0.81, 0.08], // Can be charged, but often neutral
  CYS: [0.81, 0.81, 0.08],
  ASN: [0.81, 0.81, 0.08],
  GLN: [0.81, 0.81, 0.08],
  TRP: [0.81, 0.81, 0.08],
  // Negatively charged (red)
  ASP: [0.8, 0.1, 0.1],
  GLU: [0.8, 0.1, 0.1],
  // Positively charged (blue)
  LYS: [0.1, 0.1, 0.8],
  ARG: [0.1, 0.1, 0.8],
  // Default
  default: [0.4, 0.4, 0.4],
};

// ============================================================================
// Chain Colors (for multi-chain structures)
// ============================================================================

/**
 * Colors for different chains in a structure
 */
export const CHAIN_COLORS: RGBColor[] = [
  [0.75, 0.0, 0.0], // Red
  [0.0, 0.0, 0.75], // Blue
  [0.0, 0.75, 0.0], // Green
  [0.75, 0.75, 0.0], // Yellow
  [0.75, 0.0, 0.75], // Magenta
  [0.75, 0.38, 0.0], // Orange
  [0.0, 0.75, 0.75], // Cyan
  [0.3, 0.3, 0.3], // Grey
  [0.65, 0.1, 0.0], // Dark red
  [0.1, 0.0, 0.65], // Dark blue
  [0.0, 0.65, 0.1], // Dark green
  [0.65, 0.65, 0.2], // Olive
];

// ============================================================================
// Partial Charge Coloring
// ============================================================================

/**
 * Convert partial charge to RGB color using red-white-blue gradient
 * Negative charges → Red
 * Zero/neutral → White
 * Positive charges → Blue
 *
 * @param charge - Partial charge value (typically -1 to +1)
 * @param minCharge - Minimum charge for scaling (default -0.5)
 * @param maxCharge - Maximum charge for scaling (default +0.5)
 * @returns RGB color tuple
 */
export function chargeToColor(
  charge: number,
  minCharge: number = -0.5,
  maxCharge: number = 0.5
): RGBColor {
  // Clamp charge to range
  const clampedCharge = Math.max(minCharge, Math.min(maxCharge, charge));

  // Normalize to -1 to +1 range
  let normalized: number;
  if (clampedCharge < 0) {
    normalized = clampedCharge / Math.abs(minCharge);
  } else {
    normalized = clampedCharge / maxCharge;
  }

  // Red-White-Blue gradient
  if (normalized < 0) {
    // Negative: Red to White
    const t = 1 + normalized; // 0 to 1 as charge goes from min to 0
    return [1.0, t, t]; // Red at -1, white at 0
  } else {
    // Positive: White to Blue
    const t = 1 - normalized; // 1 to 0 as charge goes from 0 to max
    return [t, t, 1.0]; // White at 0, blue at +1
  }
}

/**
 * Alternative charge coloring: Red-Green-Blue gradient
 * More colorful alternative for visualization
 */
export function chargeToColorRGB(
  charge: number,
  minCharge: number = -0.5,
  maxCharge: number = 0.5
): RGBColor {
  const clampedCharge = Math.max(minCharge, Math.min(maxCharge, charge));

  // Normalize to 0-1 range
  const normalized = (clampedCharge - minCharge) / (maxCharge - minCharge);

  // Red → Green → Blue gradient
  if (normalized < 0.5) {
    // Red to Green
    const t = normalized * 2;
    return [1 - t, t, 0];
  } else {
    // Green to Blue
    const t = (normalized - 0.5) * 2;
    return [0, 1 - t, t];
  }
}

// ============================================================================
// Color Utility Functions
// ============================================================================

/**
 * Get element color, with fallback to default
 */
export function getElementColor(element: string): RGBColor {
  // Capitalize first letter, lowercase rest
  const normalized = element.charAt(0).toUpperCase() + element.slice(1).toLowerCase();
  return ELEMENT_COLORS[normalized] || ELEMENT_COLORS['default'];
}

/**
 * Get residue type color (hydrophobic vs polar)
 */
export function getResidueTypeColor(residueName: string): RGBColor {
  const normalized = residueName.toUpperCase();
  return RESIDUE_TYPE_COLORS[normalized] || RESIDUE_TYPE_COLORS['default'];
}

/**
 * Get residue charge color (positive/negative/neutral)
 */
export function getResidueChargeColor(residueName: string): RGBColor {
  const normalized = residueName.toUpperCase();
  return RESIDUE_CHARGE_COLORS[normalized] || RESIDUE_CHARGE_COLORS['default'];
}

/**
 * Get chain color by chain index
 */
export function getChainColor(chainIndex: number): RGBColor {
  return CHAIN_COLORS[chainIndex % CHAIN_COLORS.length];
}

/**
 * Convert RGB (0-1 range) to hex color string
 */
export function rgbToHex(rgb: RGBColor): HexColor {
  const r = Math.round(rgb[0] * 255);
  const g = Math.round(rgb[1] * 255);
  const b = Math.round(rgb[2] * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Convert hex color to RGB (0-1 range)
 */
export function hexToRgb(hex: HexColor): RGBColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return [0.5, 0.5, 0.5];
  }
  return [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
  ];
}

/**
 * Convert RGB (0-1 range) to CSS rgb() string
 */
export function rgbToCss(rgb: RGBColor): string {
  const r = Math.round(rgb[0] * 255);
  const g = Math.round(rgb[1] * 255);
  const b = Math.round(rgb[2] * 255);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Blend two colors together
 */
export function blendColors(color1: RGBColor, color2: RGBColor, t: number): RGBColor {
  const clampedT = Math.max(0, Math.min(1, t));
  return [
    color1[0] + (color2[0] - color1[0]) * clampedT,
    color1[1] + (color2[1] - color1[1]) * clampedT,
    color1[2] + (color2[2] - color1[2]) * clampedT,
  ];
}

/**
 * Lighten a color by blending with white
 */
export function lighten(color: RGBColor, amount: number): RGBColor {
  return blendColors(color, [1, 1, 1], amount);
}

/**
 * Darken a color by blending with black
 */
export function darken(color: RGBColor, amount: number): RGBColor {
  return blendColors(color, [0, 0, 0], amount);
}

// ============================================================================
// 3Dmol.js Color Scheme Helpers
// ============================================================================

/**
 * Create a 3Dmol.js compatible color function for partial charges
 * Returns a function that can be passed to 3Dmol's colorfunc option
 */
export function create3DmolChargeColorFunc(
  minCharge: number = -0.5,
  maxCharge: number = 0.5
): (atom: any) => number {
  return (atom: any) => {
    const charge = atom.partialCharge || 0;
    const rgb = chargeToColor(charge, minCharge, maxCharge);
    // 3Dmol expects color as a single number (0xRRGGBB)
    const r = Math.round(rgb[0] * 255);
    const g = Math.round(rgb[1] * 255);
    const b = Math.round(rgb[2] * 255);
    return (r << 16) | (g << 8) | b;
  };
}

/**
 * Create a 3Dmol.js compatible color function for residue charges
 */
export function create3DmolResidueChargeColorFunc(): (atom: any) => number {
  return (atom: any) => {
    const resName = atom.resn || '';
    const rgb = getResidueChargeColor(resName);
    const r = Math.round(rgb[0] * 255);
    const g = Math.round(rgb[1] * 255);
    const b = Math.round(rgb[2] * 255);
    return (r << 16) | (g << 8) | b;
  };
}

/**
 * Create a 3Dmol.js compatible color function for residue types
 */
export function create3DmolResidueTypeColorFunc(): (atom: any) => number {
  return (atom: any) => {
    const resName = atom.resn || '';
    const rgb = getResidueTypeColor(resName);
    const r = Math.round(rgb[0] * 255);
    const g = Math.round(rgb[1] * 255);
    const b = Math.round(rgb[2] * 255);
    return (r << 16) | (g << 8) | b;
  };
}

// ============================================================================
// Symmetry Color Exports (from symmetry-detection.ts)
// ============================================================================

// Re-export symmetry color functionality for centralized color access
export {
  SYMMETRY_COLORS,
  DEFAULT_ATOM_COLOR,
  create3DmolSymmetryColorFunc,
  create3DmolRingColorFunc,
  create3DmolRingBondsColorFunc,
} from './symmetry-detection.ts';

/**
 * Create a 3Dmol.js compatible color function for bond order visualization
 * Single bonds = grey, Double = green, Triple = blue, Aromatic = magenta
 */
export function create3DmolBondOrderColorFunc(): (atom: any) => number {
  // Bond order coloring is applied to atoms based on their highest bond order
  // This is a simplified approach - 3Dmol doesn't easily support per-bond coloring
  return (atom: any) => {
    // Check if atom has bond info
    const bonds = atom.bonds || [];
    let maxOrder = 1;

    for (const bond of bonds) {
      const order = bond.order || 1;
      if (order > maxOrder) {
        maxOrder = order;
      }
    }

    // Color based on max bond order
    switch (maxOrder) {
      case 1:
        return 0x808080; // Grey for single bonds
      case 2:
        return 0x00c000; // Green for double bonds
      case 3:
        return 0x0000c0; // Blue for triple bonds
      case 4:
      case 1.5: // Aromatic
        return 0xc000c0; // Magenta for aromatic
      default:
        return 0x808080; // Default grey
    }
  };
}
